import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { broadcasts, isApiError } from '@repletoryreach/sdk-api';
import { downloadBlob } from '@repletoryreach/sdk-utils/file';
import { Stack, Card, Group, Title, Text, Badge, Skeleton, Box, Button } from '@repletoryreach/sdk-ui';
import { Alert, Tabs, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifyInfo, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useCredits } from '../../../lib/credits';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount, formatRelative, shortId } from '../../../lib/format';
import { OverviewTab, statusColor } from './components/OverviewTab';
import { StatsTab } from './components/StatsTab';
import { LifecycleTab } from './components/LifecycleTab';
import { RecipientsTab } from './components/RecipientsTab';
import { ChannelBadge, broadcastName, hasTemplate, isEmailBroadcast, isSmsBroadcast, smsKindName, emailKindName } from '../components/channel';
import { broadcastFailureText, broadcastStatusLabel, pollInterval } from '../components/delivery';
import '../broadcasts.css';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

export default function BroadcastViewPage() {
  const { id } = useParams();
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const credits = useCredits();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const recipientStatus = params.get('status') ?? null;
  const setTab = (next: string, status?: string) => setParams((prev) => {
    const q = new URLSearchParams(prev);
    q.set('tab', next);
    if (status !== undefined) q.set('status', status);
    else if (next !== 'recipients') q.delete('status');
    return q;
  }, { replace: true });

  /*
   * The report: the funnel, the rates and the reasons (4 October 2026). It also says how many were sent with no
   * delivery report yet, which is what decides whether this page keeps asking.
   */
  const reportQ = useQuery({
    queryKey: ['broadcast', id, 'report'],
    queryFn: () => broadcasts.getBroadcastReport(id!),
    enabled: !!id,
    retry: false,
    refetchInterval: (q) => pollInterval(
      qc.getQueryData<Awaited<ReturnType<typeof broadcasts.getBroadcast>>>(['broadcast', id])?.data,
      q.state.data?.data.awaiting_report
    )
  });
  const report = reportQ.data?.data ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['broadcast', id],
    queryFn: () => broadcasts.getBroadcast(id!),
    enabled: !!id,
    /*
     * This used to stop at `finished_at` on the grounds that a finished broadcast cannot change. It can: delivery
     * and read reports, and failures reported late, keep arriving for hours (4 October 2026). It asks every five
     * seconds while sending, then every thirty while reports are still due, for up to 48 hours.
     */
    refetchInterval: (q) => pollInterval(q.state.data?.data, reportQ.data?.data.awaiting_report)
  });
  const b = data?.data;
  const poll = pollInterval(b, report?.awaiting_report);

  // The broadcast this one resends, named rather than shown as an id.
  const sourceQ = useQuery({
    queryKey: ['broadcast', b?.resent_from_id],
    queryFn: () => broadcasts.getBroadcast(b!.resent_from_id!),
    enabled: !!b?.resent_from_id,
    staleTime: 5 * 60_000,
    retry: false
  });

  const download = useMutation({
    mutationFn: () => broadcasts.downloadBroadcastRecipients(id!),
    onSuccess: (blob) => downloadBlob(blob, `broadcast-${id}-results.csv`),
    onError: () => notifyError(t('broadcasts.download.failed'), t('broadcasts.download.title'))
  });

  const resend = useMutation({
    mutationFn: () => broadcasts.resendFailed(id!),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      const r = res.data;
      notifySuccess(
        r.skipped > 0
          ? t('broadcasts.resend.doneSkipped', { count: formatCount(r.recipients_total), skipped: formatCount(r.skipped) })
          : t('broadcasts.resend.done', { count: formatCount(r.recipients_total) }),
        t('broadcasts.resend.title')
      );
      nav(`/broadcasts/${r.id}`);
    },
    onError: (err) => {
      if (isApiError(err) && err.code === 'NOTHING_TO_RESEND') {
        notifyInfo(t('broadcasts.resend.nothing'), t('broadcasts.resend.title'));
        return;
      }
      if (isApiError(err) && err.code === 'BROADCAST_NOT_FINISHED') {
        notifyInfo(t('broadcasts.resend.notFinished'), t('broadcasts.resend.title'));
        return;
      }
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('broadcasts.resend.title'));
    }
  });

  if (isLoading) return <Skeleton height={140} />;
  if (error) return <Alert color="red">{(error as Error).message}</Alert>;
  if (!b) return <Alert color="warning">{t('broadcasts.view.notFound')}</Alert>;

  const failure = broadcastFailureText(t, b);
  const failedCount = report?.failed ?? b.recipients_failed;
  const canResend = !!b.finished_at && failedCount > 0;

  function confirmResend() {
    openConfirmModal({
      title: t('broadcasts.resend.confirmTitle'),
      children: (
        <Stack gap={8}>
          <Text fz="sm">{t('broadcasts.resend.confirmBody', { count: formatCount(failedCount) })}</Text>
          <Text fz="sm" c={MUTED}>{t('broadcasts.resend.confirmLeftOut')}</Text>
        </Stack>
      ),
      labels: { confirm: t('broadcasts.resend.confirm'), cancel: t('action.cancel') },
      onConfirm: () => resend.mutate()
    });
  }

  return (
    <Stack gap="md">
      <BreadcrumbTrail
        items={[{ label: t('broadcasts.title'), href: '/broadcasts' }, { label: broadcastName(t, b) }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />
      <Card withBorder>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group gap="sm" align="baseline">
              <Title order={2}>{broadcastName(t, b)}</Title>
              <ChannelBadge broadcast={b} t={t} />
              <Badge size="sm" variant="light" color={statusColor(b.status_code)} data-testid="broadcast-status">
                {broadcastStatusLabel(t, b.status_code)}
              </Badge>
              {hasTemplate(b) && <Badge size="xs" variant="light">{b.template_language}</Badge>}
            </Group>
            <Text fz="sm" c={MUTED}>
              {t('broadcasts.view.header.summary', { sent: b.recipients_sent, total: b.recipients_total, failed: b.recipients_failed, filtered: b.recipients_filtered })}
              {b.estimated_cost_minor != null && (
                <> · {t('broadcasts.view.header.estimate', { amount: credits(b.estimated_cost_minor, b.estimated_currency ?? undefined) })}</>
              )}
              {isEmailBroadcast(b)
                ? <> · {emailKindName(t, b)}</>
                : isSmsBroadcast(b)
                ? <> · {smsKindName(t, b)}</>
                : b.template_category && <> · {t(`broadcasts.category.${b.template_category}`, b.template_category)}</>}
            </Text>
            <Text fz="xs" c={MUTED}>
              {t('broadcasts.view.header.id')} <code>{shortId(b.id)}</code> · {t('broadcasts.view.header.created', { when: formatRelative(b.created_at) })}
              {b.resent_from_id && (
                <>
                  {' · '}
                  {t('broadcasts.view.header.resentFrom')}{' '}
                  <Link to={`/broadcasts/${b.resent_from_id}`} data-testid="broadcast-resent-from">
                    {sourceQ.data?.data ? broadcastName(t, sourceQ.data.data) : t('broadcasts.view.header.resentFromEarlier')}
                  </Link>
                </>
              )}
            </Text>
            {poll !== false && b.finished_at && (
              <Text fz="xs" c={MUTED}>{t('broadcasts.view.header.stillReporting')}</Text>
            )}
          </Stack>
          <Group gap="xs" wrap="wrap">
            {b.started_at && (
              <Button size="xs" variant="default" loading={download.isPending} onClick={() => download.mutate()}>
                {t('broadcasts.download.action')}
              </Button>
            )}
            {canResend && (
              <Button size="xs" loading={resend.isPending} onClick={confirmResend}>
                {t('broadcasts.resend.action', { count: formatCount(failedCount) })}
              </Button>
            )}
          </Group>
        </Group>
      </Card>

      {/* Failed as a whole (4 October 2026): a scheduled broadcast the credit could not cover never started, and one
          nobody was reached by is not "completed". Said above everything else, with the way out. */}
      {failure && (
        <Alert color="error" role="alert" title={t('broadcasts.failure.title')} data-testid="broadcast-failure">
          <Stack gap={6}>
            <Text role="bodySmall">{failure}</Text>
            {b.failure_code === 'INSUFFICIENT_CREDITS' ? (
              <Group gap="xs">
                <Button size="xs" onClick={() => nav('/billing')}>{t('broadcasts.failure.buyCredit')}</Button>
              </Group>
            ) : b.status_code === 'failed' && failedCount > 0 ? (
              <Group gap="xs">
                <Button size="xs" variant="default" onClick={() => setTab('recipients', 'failed')}>{t('broadcasts.failure.seeWhy')}</Button>
              </Group>
            ) : null}
          </Stack>
        </Alert>
      )}

      <Tabs value={tab} onChange={(next) => setTab(next)}>
        <Tabs.List>
          <Tabs.Tab value="overview">{t('common.overview')}</Tabs.Tab>
          <Tabs.Tab value="recipients">
            {t('broadcasts.view.tabs.recipients')}
            {failedCount > 0 && (
              <Badge size="xs" color="red" variant="light" style={{ marginLeft: 6 }}>{failedCount}</Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="stats">{t('broadcasts.view.tabs.stats')}</Tabs.Tab>
          <Tabs.Tab value="lifecycle">{t('broadcasts.view.tabs.lifecycle')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview">
          <Box py="md">
            <OverviewTab
              broadcast={b}
              report={report}
              reportLoading={reportQ.isPending}
              poll={poll}
              onShowRecipients={(status) => setTab('recipients', status)}
            />
          </Box>
        </Tabs.Panel>
        <Tabs.Panel value="recipients">
          <Box py="md">
            <RecipientsTab
              broadcastId={b.id}
              channel={b.channel ?? 'whatsapp'}
              failedCount={failedCount}
              status={recipientStatus}
              onStatus={(s) => setTab('recipients', s)}
              poll={poll}
            />
          </Box>
        </Tabs.Panel>
        <Tabs.Panel value="stats"><Box py="md"><StatsTab broadcastId={b.id} poll={poll} /></Box></Tabs.Panel>
        <Tabs.Panel value="lifecycle"><Box py="md"><LifecycleTab broadcast={b} /></Box></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

